import Logger from 'wanderu-logger';

import { applyDiscountCode, createCheckoutForm } from './api';
import { createAppSlice } from './createAppSlice';
import { parseApiError } from './errors';

import type { CreateFormRequest } from './api';

const logger = Logger.getLogger('wanderu-orders:canopyCheckoutSlice');

// RD-6: bounds the Book-click order-creation call so a slow/hung orders
// service can never leave the traveler stuck -- FR-4 requires it resolve to
// a failure (and therefore the legacy fallback) within this window.
const CANOPY_CHECKOUT_TIMEOUT_MS = 5000;

interface CanopyCheckoutRejectedPayload {
    category: string;
    code: string;
    message: string;
}

// FR-1/§6.1: discountCode is kept structurally outside `request` so it
// cannot leak into the POST public/checkout/v1/form body (AC-9) -- a prior
// spec draft merged it into CreateFormRequest, which a spec review round
// caught as a request-shape hazard.
interface RequestCanopyCheckoutOrderRequest {
    discountCode?: string;
    request: CreateFormRequest;
}

// Deliberately separate from ordersSlice (RD-3): this order attempt never
// dispatches/reacts to orderIdReceived, so the discount/detailedPricing
// listener-middleware side effects in listeners.ts never fire for it.
type CanopyCheckoutState =
    | { orderId: null; status: 'idle' }
    | { orderId: null; status: 'pending' }
    | { orderId: string; status: 'succeeded' }
    | { orderId: null; status: 'failed' };

const initialState: CanopyCheckoutState = { orderId: null, status: 'idle' };

/**
 * Extracts a structured checkout error from an unknown thrown value. The
 * checkout-v1 error envelope (CheckoutError) is the full response body
 * ({code, category, details}) -- unlike the legacy orders API's nested
 * response.error shape (see errors.ts / parseApiError).
 */
function parseCheckoutError(error: unknown): CanopyCheckoutRejectedPayload {
    const responseBody = error != null && typeof error === 'object' && 'responseBody' in error
        ? (error as Record<string, unknown>).responseBody as Record<string, unknown> | undefined
        : undefined;
    const code = typeof responseBody?.code === 'string'
        ? responseBody.code
        : 'UNKNOWN';
    const category = typeof responseBody?.category === 'string'
        ? responseBody.category
        : 'SYSTEM_ERROR';
    const message = error instanceof Error
        ? error.message
        : String(error);
    return { category, code, message };
}

// Local to this file, built on parseApiError (errors.ts) rather than api.ts's
// DiscountCodeError type -- that type names codes the discount-apply endpoint
// has never emitted (see spec discount-canopy-trip-summary-1156 §6.2). This
// deliberately duplicates a small known-codes set rather than importing
// discountSlice.ts's (wrong) one, keeping the Canopy path free of any
// discountSlice/listeners.ts import (RD-2 of that spec).
type CanopyDiscountErrorCode =
    | 'ORDER_NOT_FOUND' | 'DISCOUNT_NOT_APPLICABLE' | 'UNKNOWN' // real service codes
    | 'UNKNOWN_ERROR' // parseApiError's own fallback for a response that matches neither
    | 'CLIENT_TIMEOUT'; // this thunk's own abort marker, not a server response

const KNOWN_CANOPY_DISCOUNT_ERROR_CODES = new Set<
'ORDER_NOT_FOUND' | 'DISCOUNT_NOT_APPLICABLE' | 'UNKNOWN'
>([
    'ORDER_NOT_FOUND', 'DISCOUNT_NOT_APPLICABLE', 'UNKNOWN',
]);

/**
 * Extracts a structured discount-apply error from an unknown thrown value.
 * Named distinctly from discountSlice's parseDiscountError to avoid
 * identifier collision/confusion between the two -- they target different
 * endpoint contracts (see spec §6.2).
 */
function parseCanopyDiscountError(
    error: unknown,
    aborted: boolean,
): { errorCode: CanopyDiscountErrorCode; message: string; unrecognizedCode?: string } {
    if (aborted) {
        return { errorCode: 'CLIENT_TIMEOUT', message: 'applyDiscountCode timed out' };
    }
    return parseApiError(error, {
        expectedCategories: ['CLIENT_ERROR', 'SYSTEM_ERROR'],
        knownCodes: KNOWN_CANOPY_DISCOUNT_ERROR_CODES,
    });
}

const canopyCheckoutSlice = createAppSlice({
    initialState,
    name: 'canopyCheckout',
    reducers: (create) => ({
        canopyCheckoutReset: create.reducer(() => initialState),
        requestCanopyCheckoutOrder: create.asyncThunk(
            async (
                { discountCode, request }: RequestCanopyCheckoutOrderRequest,
                { rejectWithValue },
            ) => {
                const controller = new AbortController();
                const timeoutId = setTimeout(
                    () => controller.abort(),
                    CANOPY_CHECKOUT_TIMEOUT_MS,
                );
                // AC-9/FR-5: reconstructed from `request` once, up front, so
                // both the create-form POST body and every log call below use
                // only the four known CreateFormRequest fields -- discountCode
                // can never leak into either, independent of whether some
                // future caller merges an extra field into `request` before
                // dispatching (this repo has no tsc/checkJs enforcement to
                // catch that).
                const {
                    currency, itineraryId, locale, passengerCount,
                } = request;
                try {
                    const requestOpts = { signal: controller.signal };
                    const { orderId } = await createCheckoutForm(
                        {
                            currency, itineraryId, locale, passengerCount,
                        },
                        requestOpts,
                    );
                    logger.info('requestCanopyCheckoutOrder', {
                        currency, itineraryId, locale, orderId, passengerCount,
                    });
                    // Trimmed here -- and an empty/whitespace-only result
                    // treated as absent -- as the authoritative enforcement
                    // point regardless of caller, mirroring the same rule
                    // BookButton.jsx's resolveDiscountCode already applies
                    // at the UI layer (spec §6.2/FR-2).
                    const trimmedDiscountCode = discountCode?.trim();
                    if (trimmedDiscountCode) {
                        // FR-3/FR-4/RD-1: nested try/catch, own from
                        // createCheckoutForm's -- a discount-apply failure or
                        // timeout is logged (FR-5, §6.4) but never rejects the
                        // thunk. The order already exists at this point, so
                        // the traveler still proceeds to Canopy checkout
                        // (RD-3 of this spec), just without the discount.
                        // Shares the same AbortController/timeout budget as
                        // order creation -- no second timer (FR-3).
                        try {
                            await applyDiscountCode(orderId, trimmedDiscountCode, requestOpts);
                            logger.info('requestCanopyCheckoutOrder:discountApply', { orderId });
                        } catch (discountError: unknown) {
                            const parsedDiscountError = parseCanopyDiscountError(
                                discountError,
                                controller.signal.aborted,
                            );
                            logger.error('requestCanopyCheckoutOrder:discountApply', {
                                orderId,
                                ...parsedDiscountError,
                            });
                        }
                    }
                    return { orderId };
                } catch (error: unknown) {
                    // A hung request only ever aborts via this thunk's own
                    // timeout (RD-6) -- no other caller holds the controller
                    // -- so distinguish it from a real backend error/network
                    // failure rather than logging it as an opaque UNKNOWN.
                    const parsed = controller.signal.aborted
                        ? { category: 'SYSTEM_ERROR', code: 'CLIENT_TIMEOUT', message: 'requestCanopyCheckoutOrder timed out' }
                        : parseCheckoutError(error);
                    logger.error('requestCanopyCheckoutOrder', {
                        currency, itineraryId, locale, passengerCount, ...parsed,
                    });
                    return rejectWithValue(parsed);
                } finally {
                    clearTimeout(timeoutId);
                }
            },
            {
                fulfilled: (_state, action) => ({
                    orderId: action.payload.orderId,
                    status: 'succeeded',
                }) satisfies CanopyCheckoutState,
                pending: () => ({
                    orderId: null,
                    status: 'pending',
                }) satisfies CanopyCheckoutState,
                rejected: () => ({
                    orderId: null,
                    status: 'failed',
                }) satisfies CanopyCheckoutState,
            },
        ),
    }),
    selectors: {
        selectCanopyCheckoutOrderId: (state) => state.orderId,
        selectCanopyCheckoutStatus: (state) => state.status,
    },
});

export type {
    CanopyCheckoutRejectedPayload, CanopyCheckoutState, RequestCanopyCheckoutOrderRequest,
};
// parseCanopyDiscountError is intentionally NOT part of this package's public
// API (not re-exported from index.ts) -- it is exported at module scope only
// so this file's co-located test suite can exercise it directly, matching
// the existing parseCheckoutError pattern below. discountSlice.ts and
// listeners.ts must never import it (spec §5, RD-2).
export { CANOPY_CHECKOUT_TIMEOUT_MS, parseCanopyDiscountError, parseCheckoutError };
export const { canopyCheckoutReset, requestCanopyCheckoutOrder } = canopyCheckoutSlice.actions;
export const canopyCheckoutReducer = canopyCheckoutSlice.reducer;
export const getCanopyCheckoutSelectors = canopyCheckoutSlice.getSelectors;
export const {
    selectCanopyCheckoutOrderId,
    selectCanopyCheckoutStatus,
} = canopyCheckoutSlice.selectors;
